import { Link, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, Heart } from "lucide-react";
import { useCart } from "@/hooks/useCart";
import { useDatabaseFavorites } from "@/hooks/useDatabaseFavorites";
import HeaderSearch from "./HeaderSearch";

// 128×128 transparent WebP (~6 KB), served from our own origin — displays at h-8 (32px) here.
// The 320px original /logo-header.webp is kept for other consumers.
const LOGO_SRC = "/logo-header-128.webp";

interface MobileHeaderContentProps {
  onSearch: (value: string) => void;
  onMenuOpen: () => void;
}

export const MobileHeaderContent = ({ onSearch, onMenuOpen }: MobileHeaderContentProps) => {
  const { cartItems } = useCart();
  const { favorites } = useDatabaseFavorites();
  const location = useLocation();

  const shouldShowSearch = !location.pathname.startsWith("/products") || location.search;

  return (
    <>
      {/* Mobile Header */}
      <div className="flex md:hidden justify-between items-center gap-2 py-2">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <Link to="/" className="flex-shrink-0">
            <img
              src={LOGO_SRC}
              alt="Kooperasie Veevoer Winkel"
              className="h-8 w-auto flex-shrink-0 object-contain"
              width={32}
              height={32}
              loading="eager"
              decoding="async"
            />
          </Link>
          <h1 className="text-springbok-600 font-playfair font-bold text-xs sm:text-sm md:text-base uppercase tracking-tight leading-tight">
            KOOPERASIE VEEVOER WINKEL
          </h1>
        </div>

        <div className="flex items-center space-x-1 flex-shrink-0">
          {shouldShowSearch && (
            <div className="hidden sm:block">
              <HeaderSearch onSearch={onSearch} />
            </div>
          )}

          {/* Favourites */}
          <Link to="/favorites" className="p-2 hover:text-springbok-500 transition-colors relative">
            <Heart size={20} />
            {favorites.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-springbok-500 text-white rounded-full text-xs w-5 h-5 flex items-center justify-center">
                {favorites.length}
              </span>
            )}
          </Link>

          {/* Cart */}
          <Link to="/cart" className="p-2 relative">
            <ShoppingCart size={20} />
            {cartItems?.reduce((sum, item) => sum + (item.quantity || 1), 0) > 0 && (
              <span className="absolute -top-1 -right-1 bg-gold-400 text-springbok-800 rounded-full text-xs w-5 h-5 flex items-center justify-center">
                {cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0)}
              </span>
            )}
          </Link>

          {/* Menu */}
          <button onClick={onMenuOpen} className="p-2 text-springbok-800 flex items-center gap-1">
            <Menu size={24} />
            <span className="text-sm">Menu</span>
          </button>
        </div>
      </div>

      {/* Mobile Search Bar - Below header on small screens */}
      {shouldShowSearch && (
        <div className="block sm:hidden py-2">
          <HeaderSearch onSearch={onSearch} />
        </div>
      )}
    </>
  );
};
